import { Injectable } from '@angular/core';
import { MarkerRenderService } from './marker-render.service';

@Injectable({
  providedIn: 'root'
})
export class NameLabelsService {

  private map!: google.maps.Map;
  private nameLabels = new Map<number, google.maps.marker.AdvancedMarkerElement>();
  private zoomChangedListener: google.maps.MapsEventListener | null = null;
  private clusterDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private markerRenderService: MarkerRenderService) {}

  setMap(map: google.maps.Map): void {
    this.map = map;
  }

  async render(stops: Map<number, any>): Promise<void> {
    this.clear();
    const AdvancedMarkerElement = await this.markerRenderService.getAdvancedMarkerElementClass();
    stops.forEach((stop) => {
      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: stop.lat, lng: stop.lng },
        content: this.buildLabelElement(`${stop.code ? `${stop.code} - ` : ''}${stop.name}`),
        zIndex: 9999
      });
      this.nameLabels.set(stop.id, marker);
    });
    this.zoomChangedListener = this.map.addListener('zoom_changed', () => {
      if (this.clusterDebounceTimer) clearTimeout(this.clusterDebounceTimer);
      this.clusterDebounceTimer = setTimeout(() => this.cluster(stops), 150);
    });
    this.cluster(stops);
  }

  update(id: number, stops: Map<number, any>): void {
    const stop = stops.get(id);
    if (!stop) return;
    const labelText = `${stop.code ? `${stop.code} - ` : ''}${stop.name ?? ''}`;
    const existing = this.nameLabels.get(id);
    if (existing) {
      existing.replaceChildren(this.buildLabelElement(labelText));
      this.cluster(stops);
    } else {
      this.markerRenderService.getAdvancedMarkerElementClass().then((AdvancedMarkerElement) => {
        const marker = new AdvancedMarkerElement({
          map: this.map,
          position: { lat: stop.lat, lng: stop.lng },
          content: this.buildLabelElement(labelText),
          zIndex: 9999
        });
        this.nameLabels.set(id, marker);
        this.cluster(stops);
      });
    }
  }

  setPosition(id: number, position: google.maps.LatLng): void {
    const label = this.nameLabels.get(id);
    if (label) label.position = position;
  }

  updatePosition(id: number, position: google.maps.LatLng, stops: Map<number, any>): void {
    this.setPosition(id, position);
    if (this.nameLabels.has(id)) this.cluster(stops);
  }

  clear(): void {
    this.nameLabels.forEach((m) => (m.map = null));
    this.nameLabels.clear();
    this.zoomChangedListener?.remove();
    this.zoomChangedListener = null;
    if (this.clusterDebounceTimer) {
      clearTimeout(this.clusterDebounceTimer);
      this.clusterDebounceTimer = null;
    }
  }

  cluster(stops: Map<number, any>): void {
    const zoom = this.map.getZoom() ?? 12;
    const scale = (256 * Math.pow(2, zoom)) / 360;
    const priority = (s: any) => (s.start ? 0 : s.end ? 1 : s.inflexion ? 2 : 3);
    const sorted = Array.from(stops.values()).sort((a, b) => priority(a) - priority(b));

    if (zoom >= 19) {
      const labelOffsets = new Map<number, { x: number, y: number }>();

      for (let i = 0; i < sorted.length; i++) {
        if (labelOffsets.has(sorted[i].id)) continue;
        for (let j = i + 1; j < sorted.length; j++) {
          if (labelOffsets.has(sorted[j].id)) continue;
          if (this.labelsCollide(sorted[i], sorted[j], scale)) {
            labelOffsets.set(sorted[j].id, this.computeAvoidanceOffset(sorted[i], sorted[j], scale));
          }
        }
      }

      this.nameLabels.forEach((marker, id) => {
        marker.map = this.map;
        const el = marker.content as HTMLElement;
        if (el) {
          const offset = labelOffsets.get(id);
          el.style.transform = offset ? `translate(${offset.x}px, ${offset.y}px)` : '';
        }
      });
      return;
    }

    const suppressed = new Set<number>();
    for (let i = 0; i < sorted.length; i++) {
      if (suppressed.has(sorted[i].id)) continue;
      for (let j = i + 1; j < sorted.length; j++) {
        if (suppressed.has(sorted[j].id)) continue;
        if (this.labelsCollide(sorted[i], sorted[j], scale)) suppressed.add(sorted[j].id);
      }
    }

    this.nameLabels.forEach((marker, id) => {
      marker.map = suppressed.has(id) ? null : this.map;
      const el = marker.content as HTMLElement;
      if (el) el.style.transform = '';
    });
  }

  private buildLabelElement(name: string): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'stop-label-wrapper';
    const label = document.createElement('div');
    label.className = 'stop-label';
    label.textContent = name || '';
    wrapper.appendChild(label);
    return wrapper;
  }

  private estimateLabelWidth(stop: any): number {
    const text = `${stop.code ? `${stop.code} - ` : ''}${stop.name ?? ''}`;
    return text.length * 6.5; // 12px bold font ≈ 6.5px avg char width
  }

  private labelsCollide(a: any, b: any, scale: number): boolean {
    const LABEL_HEIGHT = 14; // rendered height of a 12px font (font-size × 1.2 line-height)
    const midLatRad = ((a.lat + b.lat) / 2) * (Math.PI / 180);
    const dx = Math.abs((b.lng - a.lng) * Math.cos(midLatRad) * scale);
    const dy = Math.abs((b.lat - a.lat) * scale);
    const widthThreshold = (this.estimateLabelWidth(a) + this.estimateLabelWidth(b)) / 2;
    return dx < widthThreshold && dy < LABEL_HEIGHT;
  }

  private computeAvoidanceOffset(host: any, collider: any, scale: number): { x: number, y: number } {
    const LABEL_HEIGHT = 16;
    const dyScreen = -((collider.lat - host.lat) * scale);
    const direction = dyScreen > 0 ? 1 : -1;
    const overlapY = LABEL_HEIGHT - Math.abs(dyScreen);
    return { x: 0, y: direction * (overlapY + 2) };
  }
}
